import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { opsApi } from '../../api/client';
import { Truck, RefreshCw, ExternalLink } from 'lucide-react';

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-700',
  shipped: 'bg-blue-100 text-blue-700',
  delivered: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
};

/**
 * Ops Shipments — primary data now comes from the v3 canonical endpoint
 * `/api/admin/aa/shipments`, with the legacy `/ops/aa/shipments`
 * collection kept as a fallback for historical visibility only.
 *
 * When v3 is configured the page renders AA-live shipments (with local
 * order linkage resolved server-side); otherwise it falls back to the
 * legacy MongoDB-backed list. The active source is always labelled on
 * screen so operators know which one they're looking at.
 */
export default function OpsShipments() {
  const [v3Rows, setV3Rows] = useState(null);        // null = not yet attempted
  const [v3Summary, setV3Summary] = useState(null);  // { matched, unmatched, truncated, totalAvailable }
  const [legacyRows, setLegacyRows] = useState([]);
  const [source, setSource] = useState('loading');   // "v3" | "legacy" | "loading"
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // All three loaders only call stable React setters (which never
  // change identity) and module-level `opsApi` — the empty / minimal
  // dep arrays below are correct, not a stale-closure shortcut.
  const loadV3 = useCallback(async () => {
    try {
      const { data } = await opsApi.aaV3Shipments({ limit: 25 });
      if (data.ok) {
        setV3Rows(data.items || []);
        setV3Summary({
          matched: data.matched ?? 0,
          unmatched: data.unmatched ?? 0,
          truncated: !!data.truncated,
          totalAvailable: data.totalAvailable ?? data.count ?? 0,
        });
        setSource('v3');
        setMessage(`${data.count} live from AA v3 · ${data.matched ?? 0} matched · ${data.unmatched ?? 0} unmatched`);
        return true;
      }
      setMessage(data.message || 'AA v3 returned ok=false');
    } catch (e) {
      setMessage(e.response?.status === 503 ? 'AA v3 not configured' : `AA v3 unreachable (${e.response?.status || 'net'})`);
    }
    return false;
  }, []);

  const loadLegacy = useCallback(async () => {
    const { data } = await opsApi.aaShipments();
    setLegacyRows(data || []);
  }, []);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    const ok = await loadV3();
    if (!ok) {
      setSource('legacy');
      await loadLegacy();
    }
    setRefreshing(false);
  }, [loadV3, loadLegacy]);

  useEffect(() => {
    (async () => {
      const ok = await loadV3();
      if (!ok) {
        setSource('legacy');
        await loadLegacy();
      }
      setLoading(false);
    })();
  }, [loadV3, loadLegacy]);

  const rows = source === 'v3' ? (v3Rows || []) : legacyRows;

  return (
    <div data-testid="ops-shipments" className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Shipments</h1>
          <p className="text-sm text-gray-500 mt-1">
            Fulfilment and delivery tracking.{' '}
            <span data-testid="ship-source-tag" className={`inline-block ml-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${source === 'v3' ? 'bg-indigo-50 text-indigo-700' : source === 'legacy' ? 'bg-slate-100 text-slate-600' : 'bg-gray-100 text-gray-400'}`}>
              {source === 'v3' ? 'AA v3 · live' : source === 'legacy' ? 'Legacy · historical' : 'Loading…'}
            </span>
            {message && <span className="ml-2 text-xs text-gray-400">{message}</span>}
          </p>
        </div>
        <button
          data-testid="ship-refresh-btn"
          onClick={refresh} disabled={refreshing}
          className="px-4 py-2 border border-gray-200 rounded-lg text-xs font-semibold hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1.5"
        >
          <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="space-y-2">{[...Array(4)].map((_, i) => <div key={`shipments-skeleton-${i}`} className="h-12 bg-gray-100 rounded-lg animate-pulse" />)}</div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Truck className="w-10 h-10 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-sm">No shipments yet.</p>
          {source !== 'v3' && (
            <p className="text-gray-400 text-xs mt-1">
              Shipments appear here once Active Ants fulfilment is connected and processing orders.
            </p>
          )}
        </div>
      ) : source === 'v3' ? (
        <>
          {v3Summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="bg-white rounded-xl border border-gray-200 p-4"><p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Fetched</p><p className="text-2xl font-bold text-gray-900 mt-1">{rows.length}</p></div>
              <div className="bg-white rounded-xl border border-gray-200 p-4"><p className="text-xs text-green-600 uppercase tracking-wider font-semibold">Matched locally</p><p className="text-2xl font-bold text-gray-900 mt-1">{v3Summary.matched}</p></div>
              <div className="bg-white rounded-xl border border-gray-200 p-4"><p className="text-xs text-amber-700 uppercase tracking-wider font-semibold">Unmatched</p><p className="text-2xl font-bold text-gray-900 mt-1">{v3Summary.unmatched}</p></div>
              <div className={`rounded-xl border p-4 ${v3Summary.truncated ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200'}`}>
                <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Total in AA</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{v3Summary.totalAvailable}</p>
                {v3Summary.truncated && <p className="text-[10px] text-amber-700 mt-0.5">Showing first {rows.length}</p>}
              </div>
            </div>
          )}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">AA order id</th>
                  <th className="px-4 py-3 text-left">External ref</th>
                  <th className="px-4 py-3 text-left">Local order</th>
                  <th className="px-4 py-3 text-left">Match</th>
                  <th className="px-4 py-3 text-left">Carrier</th>
                  <th className="px-4 py-3 text-left">Tracking</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-left">Shipped at</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {rows.map((s, idx) => (
                  <tr key={`${s.aaOrderId}-${idx}`} data-testid={`ship-row-${idx}`} className="hover:bg-gray-50/50">
                    <td className="px-4 py-3 font-mono font-semibold text-gray-900">{s.aaOrderId || '—'}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-600">{s.externalReference || <span className="text-gray-300">—</span>}</td>
                    <td className="px-4 py-3 text-xs">
                      {s.localOrder ? (
                        <Link to={`/ops/orders/${s.localOrder._id}`} className="inline-flex items-center gap-1 text-indigo-700 hover:text-indigo-900 font-mono">
                          #{s.localOrder.orderNumber} <ExternalLink className="w-3 h-3" />
                        </Link>
                      ) : <span className="text-gray-300">no match</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
                        s.matchSource === 'external-ref' ? 'bg-green-100 text-green-700'
                        : s.matchSource === 'local-aa-order-id' ? 'bg-blue-100 text-blue-700'
                        : s.matchSource === 'legacy-sync' ? 'bg-slate-100 text-slate-600'
                        : 'bg-gray-100 text-gray-400'
                      }`}>{s.matchSource || 'none'}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{s.carrier || '—'}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-700">{s.trackingNumber || '—'}</td>
                    <td className="px-4 py-3 text-center"><span className={`text-xs font-semibold px-2 py-0.5 rounded ${STATUS_COLORS[s.status] || 'bg-gray-100 text-gray-500'}`}>{s.status}</span></td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{s.shippedAt || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        // Legacy fallback table (pre-existing shape).
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
              <tr><th className="px-4 py-3 text-left">Order</th><th className="px-4 py-3 text-left">Carrier</th><th className="px-4 py-3 text-left">Tracking</th><th className="px-4 py-3 text-center">Status</th><th className="px-4 py-3 text-left">Date</th></tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.map(s => (
                <tr key={s._id} className="hover:bg-gray-50/50">
                  <td className="px-4 py-3 font-mono font-semibold">#{s.order?.orderNumber || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{s.carrier || '—'}</td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-700">{s.trackingNumber || '—'}</td>
                  <td className="px-4 py-3 text-center"><span className={`text-xs font-semibold px-2 py-0.5 rounded ${STATUS_COLORS[s.status] || 'bg-gray-100 text-gray-500'}`}>{s.status}</span></td>
                  <td className="px-4 py-3 text-gray-500">{s.created_at ? new Date(s.created_at).toLocaleDateString('nl-BE') : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
